import CloseIcon from '@mui/icons-material/Close';
import * as Mui from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import FileUploader from "../../../../../../util/component/FileUploader";
import { ContextOperation } from "../../../../context/OperationContext";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useSnackbar from "../../../../hooks/useSnackbar";
import { AutoCompleteOption } from "../../../common/AutoComplete";
import FormInput from "../../../common/FormInput";
import { domains2AutocompleteOptions, nameof } from "../../../common/ObjectUtils";
import { ContextDamageForm } from "../../DamageFormContext";
import DamageFormService from "../../service/DamageFormService";
import { DamageFormTypes } from "../../Types";
import useDamageFormActions from "../../service/DamageFormActions";

interface Props {
    open: boolean;
    onClose: () => void;
};

const CreateAttachment = (props: Props) => {
    const { setLoading } = useContext(ContextOperation);
    const { documentTypes, attachmentTypes } = useContext(ContextDamageForm);

    const t = useOperationTranslation();

    const { id } = useParams();

    const snackBar = useSnackbar();

    const [fileUploaded, setFileUploaded] = useState<FormData | null>(null);

	const actions = useDamageFormActions();

    const form = useForm<DamageFormTypes.IAttachmentForm>({
        defaultValues: {
            damageFormId: id
        }
    });

    const onFileUpload = (file: File) => {
        const data = new FormData();
        data.append("files", file);
        setFileUploaded(data);
    }

    const onSubmit = (data: DamageFormTypes.IAttachmentForm) => {
        setLoading(true);
        data.file = fileUploaded;
        DamageFormService.details.generalInfo.attachments.post(data).then(
            () => {
                onClose();
				actions.getGeneralInfo();
                setLoading(false);
                snackBar.show("success", t("damageForm.details.generalInfo.attachments.snackbar.create.success"));
            },
            () => {
                setLoading(false);
                snackBar.show("error", t("damageForm.details.generalInfo.attachments.snackbar.create.error"));
            }
        )
    }

    const onClose = () => {
        form.reset();
        props.onClose();
    }

    const attachmentTypesOptions: AutoCompleteOption[] = useMemo<AutoCompleteOption[]>(() => {
        return domains2AutocompleteOptions(attachmentTypes || [], "id", "descr");
    }, [attachmentTypes]);

    const documentTypesOptions: AutoCompleteOption[] = useMemo<AutoCompleteOption[]>(() => {
        return documentTypes?.map((val) => ({ label: val.displayName, value: val.documentType })) || [];
    }, [documentTypes]);

    return(
        <>   
            <Mui.Dialog 
                open={props.open} 
                onClose={onClose} 
                fullWidth
                maxWidth="md" 
            >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Mui.DialogTitle>
                        <Mui.Grid container alignItems={"center"}>
                            <Mui.Grid item>
                                <Mui.Typography variant="h5">
                                    {t("damageForm.details.generalInfo.attachments.dialogs.create.title")}
                                </Mui.Typography>
                            </Mui.Grid>
                            <Mui.Grid item marginLeft={"auto"}>
                                <Mui.IconButton onClick={onClose}>
                                    <CloseIcon />
                                </Mui.IconButton>
                            </Mui.Grid>
                        </Mui.Grid>
                    </Mui.DialogTitle>
                    
                    <Mui.DialogContent>
                        <Mui.Grid container spacing={3} mb={3}>
                            <Mui.Grid item xs={12} sm={6} md={6}>
                                <FormInput
                                    name={nameof<DamageFormTypes.IAttachmentForm>("attachmentTypeId")}
                                    formInstance={form}
                                    type="select"
                                    options={attachmentTypesOptions}
                                    fullWidth
                                    label={t("damageForm.details.generalInfo.attachments.dialogs.create.lblAttachmentType")}
                                    validation={{
                                        required: "this field is required",
                                    }}
                                />
                            </Mui.Grid>
                            <Mui.Grid item xs={12} sm={6} md={6}>
                                <FormInput
                                    name={nameof<DamageFormTypes.IAttachmentForm>("documentType")}
                                    type="select"
                                    options={documentTypesOptions}
                                    formInstance={form}
                                    fullWidth
                                    label={t("damageForm.details.generalInfo.attachments.dialogs.create.lblDcoumentType")}
                                    disabled={form.watch("attachmentTypeId")===3?false:true}
                                />
                            </Mui.Grid>
                        </Mui.Grid>

                        <FileUploader onUpload={onFileUpload} />
                    </Mui.DialogContent>

                    <Mui.DialogActions>
                        <Mui.Button variant="outlined" size="smallPlus" onClick={onClose}>
                            <Mui.Typography variant="h6" color="inherit">
                                {t("damageForm.details.generalInfo.attachments.dialogs.create.btnCancel")}
                            </Mui.Typography>
                        </Mui.Button>
                        <Mui.Button type="submit" size="smallPlus">
                            <Mui.Typography variant="h6" color="inherit">
                                {t("damageForm.details.generalInfo.attachments.dialogs.create.btnSave")}
                            </Mui.Typography>
                        </Mui.Button>
                    </Mui.DialogActions>
                </form>
            </Mui.Dialog>
        </>
    )
}
export default CreateAttachment;